import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

import { hidePageProgress, hideProductModal, showLoginModal, showPageProgress } from '@/redux/slices/gui.slice';
import { addBoxProduct } from '@/redux/slices/box.slice';

import {
  setCategInfos,
  getExclusiveCellarProducts,
  refreshCategDataClear,
  getCategWithFiltersAndProductsSuccess,
  getCategWithFiltersAndProductsError,
} from '@/redux/slices/categ.slice';

import { useAppSelector, useAppDispatch } from '@/redux';
import { NS } from '@/constants';

import { useScreenSizeContext } from '@/components/ScreenSize.context';

import {
  isObjectEmpty,
  isUserEmpty,
} from '@/utils/comparisonUtils';
import { completePromises } from '@/utils/completePromises';
import { isFeatureEnabled } from '@/utils/millesimaUtils';
import { scrollToPageTop } from '@/utils/scroll';
import { getSelectedFiltersInfos, deleteFilterString, parseFilters, isNote } from '@/utils/categUtils';
import DisplayNbProducts from '@/components/categ/DisplayNbProducts';
import CategFiltersDesktop from '@/components/categ/winebox/CategFiltersDesktop';
import CategFiltersMobile from '@/components/categ/winebox/CategFiltersMobile';
import BreadcrumbContainer from '@/components/common/BreadcrumbContainer';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import ProductListingWB from '@/components/winebox/ProductListingWB';
import CategFooter from '@/components/categ/winebox/CategFooter';
import CategHeader from '@/components/categ/winebox/CategHeader';

const Pagination = dynamic(() => import('@/components/categ/Pagination'), { ssr: false });
Pagination.displayName = 'Pagination';
const ProductDetailModalWB = dynamic(() => import('@/components/winebox/ProductDetailModalWB'), { ssr: false });
ProductDetailModalWB.displayName = 'ProductDetailModalWB';

import styles from './CategViewWB.module.scss';
import { PAGE_SIZE, DEFAULT_PAGE, DEFAULT_SORT } from '@/constants/categ';
import { FEATURES } from '@/constants/features';
import { KEYS } from '@/constants/keys';
import { useServices } from '@/services/useServices';
import { LocalStorageService } from '@/services/LocalStorage.service';

type SavedWinebox = {
  item: string;
  qty: number;
  options?: {
    set?: boolean;
    showConfirm?: boolean;
  }
}

export const normalize = (value, def) => value ? +value : def;

export const CategViewWB = (props) => {
  const {
    categSSR, categId, filterQuerySSR, pageSSR, sortSSR, espot,
    originalUrl, searchMode, searchTerm, pageSize, productsForEc, ratingValue, ratingCount
  } = props;

  const descriptionRef = useRef<HTMLDivElement>();
  const topListingRef = useRef<HTMLDivElement>();
  const { datalayerService, productsService } = useServices();
  const { isMobileDevice, productDetailsModalWB, productDetailsModalWBVisible } = useAppSelector(({ gui }) => gui);
  const { refreshData } = useAppSelector(({ categ }) => categ);
  const { loggedUser } = useAppSelector(({ auth }) => auth);
  const { storeConf } = useAppSelector(({ root }) => root);
  const { t } = useTranslation([NS.COMMON, NS.CATEG]);
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [categ, setCateg] = useState(categSSR);
  const [products, setProducts] = useState(categSSR.products);
  const [pageState, setPage] = useState(pageSSR);
  const [sortState, setSort] = useState(sortSSR);
  const [nbTotalRows, setNbTotalRows] = useState(categSSR.nbTotalRows);
  const [filtersState, setFilters] = useState(categSSR.filters);
  const [filterQueryState, setFilterQuery] = useState(filterQuerySSR || '');

  const { locale, currencyISO: currency, url } = storeConf;

  const [selectedFiltersInfos, setSelectedFiltersInfos] = useState(
    filtersState ? getSelectedFiltersInfos(t, filterQueryState, filtersState, locale, currency) : []
  );

  useEffect(() => {
    setCateg(categSSR);
    setProducts(categSSR.products);
    setPage(pageSSR);
    setSort(sortSSR);
    setNbTotalRows(categSSR.nbTotalRows);
    setFilters(categSSR.filters);
    setFilterQuery(filterQuerySSR);
    setPage(pageSSR);
    setSort(sortSSR);
  }, [categSSR]);

  const isEc = isUserEmpty(loggedUser) ? false : loggedUser?.isEc;
  const isDesktop = useScreenSizeContext().isDesktop;

  useEffect(() => {
    if (refreshData) {
      dispatch(refreshCategDataClear());
      refreshView({});
    }
  }, [refreshData]);

  useEffect(() => {
    const itemsFromProductPage = LocalStorageService.getObject<SavedWinebox>(KEYS.WINEBOX) as SavedWinebox;
    if (!isObjectEmpty(itemsFromProductPage)) {
      const { item, qty, options } = itemsFromProductPage;
      dispatch(addBoxProduct({ item, qty, options }));
      LocalStorageService.remove(KEYS.WINEBOX);
    }
  }, []);

  useEffect(() => {
    const nextSelectedFiltersInfos = getSelectedFiltersInfos(t, filterQueryState, filtersState, locale, currency);

    // If we do a setState of an array, even if it's the same array, the state considers that there is a change
    // because the comparison is not complete. "stringify" allows to do a deep comparaison of array
    if (JSON.stringify(selectedFiltersInfos) !== JSON.stringify(nextSelectedFiltersInfos)) {
      setSelectedFiltersInfos(nextSelectedFiltersInfos);
    }
  }, [filtersState]);

  useEffect(() => {
    // After connection with an Exclusive Cellar account, we must get EC products
    if (isEc && !productsForEc) {
      dispatch(getExclusiveCellarProducts());
    }
  }, [isEc]);

  useEffect(() => {
    if (categ.isPrivate && !loggedUser) {
      dispatch(showLoginModal());
    }
    updateDatalayer();
  }, [categ, loggedUser]);

  useEffect(() => {
    if (isDesktop && !window.location.hash) {
      const bodyRect = document.body?.getBoundingClientRect();
      const topListingRect = topListingRef.current?.getBoundingClientRect() as DOMRect;
      const offsetTop = topListingRect?.top - bodyRect?.top;

      scrollToPageTop(selectedFiltersInfos?.length > 0 ? offsetTop : 0, 'smooth');
    }
  }, [selectedFiltersInfos, pageState]);

  useEffect(() => {
    return () => {
      dispatch(hidePageProgress());
      dispatch(hideProductModal());
    };
  }, []);

  const updateDatalayer = () => {
    const { nbTotalRows, sortOrder } = categ;

    datalayerService.updateCategDatalayer(
      products, filterQueryState, sortState, sortOrder, categ, searchMode, storeConf.storeId, nbTotalRows, searchTerm, true
    );
  };

  /**
   * Called when a filter (price, bloc, checkbox or critic) is set/unset in the filters component
   * @param {object} changes
   *  {string} nameId The filter nameId
   *  {string} valueId The filter valueId
   *  {boolean} check True to add/update, false to remove
   *  {boolean} price True if price filter
   *  {number} page The page number
   *  {string} structure The view mode
   *  {string} sort The sort mode
   *  {number} minPrice The min price criteria
   *  {number} maxPrice The max price criteria
   */
  const onFilterChange = (changes: any = {}) => {
    let filterQueryWB = changes.filterQuery ?? filterQueryState ?? '';
    const pageWB = +(changes.nameId ? DEFAULT_PAGE : changes.page ?? pageState ?? DEFAULT_PAGE);
    const sortWB = +(changes.sort ?? sortState ?? DEFAULT_SORT);

    if (changes.nameId) {
      const { nameId, valueId, check, price, minPrice, maxPrice } = changes;
      if (price || nameId === 'Price') {
        filterQueryWB = updateFilterString(filterQueryWB, 'minPrice', minPrice, !!minPrice || minPrice === 0);
        filterQueryWB = updateFilterString(filterQueryWB, 'maxPrice', maxPrice, !!maxPrice);
      } else if (isNote(nameId)) {
        filterQueryWB = updateFilterString(filterQueryWB, nameId, valueId, false);// We delete old selected critic filter
        filterQueryWB = updateFilterString(filterQueryWB, nameId, valueId, true); // And we replace it by new critic filter
      } else {
        filterQueryWB = updateFilterString(filterQueryWB, nameId, valueId, check);
      }
    }

    refreshView({ pageWB, sortWB, filterQueryWB });
  };

  /**
   * Update, add or remove filter from filter string
   * @param {string} filterString The source filter string
   * @param {string} nameId The filter name (ex: millesime, couleur)
   * @param {string} valueId The filter valueId (ex: 29 for millesime, 5 for couleur)
   * @param {boolean} check True if filter must be set, false to remove it
   * @return {string} the new filter string
   */
  const updateFilterString = (filterString, nameId, valueId, check) => {
    // Build a key/value object from the given filters string
    // Before: "filter1:v1|filter2:v2|filter3:v3|note_rp:88|note_rp:88+|note_rp:89"
    // After: { filter1: v1, filter2: v2, filter3: v3, note_rp: [note_rp:88, note_rp:88+, note_rp:89]}
    let filters = filterString.split('|').reduce((obj, item) => {
      const [key, value] = item.split(':');
      // Verify if there is already a filter for critics
      if (isNote(filterString) && isNote(key)) {
        // Need special case if the first iteration
        if (isObjectEmpty(obj)) {
          return { [key]: [`${key}:${value}`] };
        } else {
          // Create array with structure : [note_rp:88, note_rp:88+, note_rp:89]
          return { ...obj, [key]: Array.isArray(obj[key]) ? [...obj[key], `${key}:${value}`] : [obj[key], `${key}:${value}`] };
        }
      } else {
        return { ...obj, [key]: value };
      }
    }, {});

    // Add or overwrite the requested filter entry
    filters[nameId] = check ? (
      isNote(nameId) ? valueId.join('|' + nameId + ':') : valueId
    ) : undefined;

    if (isNote(nameId) && !check) {
      filters = Object.fromEntries(Object.entries(filters).filter(([key]) => !isNote(key)));
    }

    // Rebuild the filters string
    filters = Object.keys(filters).filter((k) => (!!filters[k] || filters[k] === 0)).reduce((arr, k) => [
      ...arr,
      isNote(k) && Array.isArray(filters[k]) ? filters[k].join('|') : `${k}:${filters[k]}` // Need join notes Tab before join all filters
    ], []).join('|');

    return filters.replace('||', '|');
  };

  /**
   * Refresh products list according to the current filters and parameters
   */
  const refreshView = ({ pageWB, sortWB, filterQueryWB, searchMode, searchTerm }: any) => {
    dispatch(showPageProgress());

    pageWB = pageWB ?? (pageState || DEFAULT_PAGE);
    sortWB = sortWB ?? (sortState || DEFAULT_SORT);
    filterQueryWB = filterQueryWB ?? (filterQueryState || '');
    searchMode = searchMode ?? (props.searchMode || false);
    searchTerm = searchTerm ?? (props.searchTerm || '');

    const queryParams = [
      pageWB !== DEFAULT_PAGE && `page=${pageWB}`,
      sortWB !== DEFAULT_SORT && `sort=${sortWB}`,
      filterQueryWB !== '' && `filter=${filterQueryWB}`
    ].filter((p) => !!p).join('&');

    let url = searchMode ? `/search?searchTerm=${searchTerm}` : `/${router.query.slug}`;

    if (queryParams) {
      url += `${url.includes('?') ? '&' : '?'}${queryParams}`;
    }

    // Shallow routing just changes the URL, but the page don't reload itself
    router.push(router.pathname, url, { shallow: true });

    productsService.getCategWithFiltersAndProducts({
      storeId: storeConf.storeId,
      categ: categId,
      page: pageWB,
      size: PAGE_SIZE.LIST,
      filter: filterQueryWB,
      sort: sortWB,
      isCategViewWB: true,
      ec: !!loggedUser?.isEc
    }).then((result: any) => {
      setCateg(result.categ);
      setProducts(result.products);
      setPage(pageWB);
      setSort(parseInt(result.sortOrder));
      setNbTotalRows(result.nbTotalRows);
      setFilters(parseFilters(result.filters, filterQueryWB, storeConf.storeId));
      setFilterQuery(filterQueryWB);

      dispatch(hidePageProgress());
      dispatch(getCategWithFiltersAndProductsSuccess({
        content: {
          ...result.categ,
          top: result.top,
          filters: parseFilters(result.filters, filterQueryWB, storeConf.storeId),
          products: result.products,
          nbTotalRows: result?.nbTotalRows,
          sortOrder: result?.sortOrder
        }
      }));
    }).catch((error) => {
      dispatch(hidePageProgress());
      dispatch(getCategWithFiltersAndProductsError({ error }));
    });
  };

  const onSortChange = (value) => {
    onFilterChange({ page: DEFAULT_PAGE, sort: value });
  };

  const onPageChange = (page) => {
    onFilterChange({ page });
  };

  const deleteFilter = (tag) => {
    if (isNote(tag.nameId)) {
      onFilterChange({ filterQuery: deleteFilterString(filterQueryState, tag) });
    } else {
      onFilterChange({ ...tag, check: false });
    }
  };

  const urlBreadcrumb = isDesktop ? url : originalUrl;

  const deleteAll = () => {
    onFilterChange({ filterQuery: '' });
  };

  return (
    <>
      <MetaTagsMillesima
        categ={categ}
        nbTotalRows={nbTotalRows}
        page={pageState}
        pageSize={pageSize}
        searchMode={searchMode}
        originalUrl={originalUrl}
        isMobileDevice={isMobileDevice}
      />

      <div data-test-id="categPage" className={styles['categ-page']}>
        {categ.htmlDepartementPage && (
          <div
            data-test-id="htmlDepartementPage"
            className={styles['departement-page']}
            dangerouslySetInnerHTML={{ __html: categ.htmlDepartementPage }}
          />
        )}
        <CategHeader
          categ={categ}
          descriptionRef={descriptionRef}
          isDesktop={isDesktop}
          isMobileDevice={isMobileDevice}
        />
        <div className={styles['grid-container']}>
          {isDesktop ? (
            <CategFiltersDesktop
              selectedFiltersInfos={selectedFiltersInfos}
              deleteFilter={deleteFilter}
              onSortChange={onSortChange}
              categId={categId}
              sort={sortState}
              topCategory={categ.topCategory}
              filters={filtersState}
              nbTotalRows={nbTotalRows}
              onFilterChange={onFilterChange}
              searchTerm={searchTerm}
              topListingRef={topListingRef}
              customClassName={{ filters: styles.filters, header: styles.header, tags: styles.tags }}
              onDeleteAll={deleteAll}
            />
          ) : (
            <>
              <CategFiltersMobile
                filters={filtersState}
                deleteFilter={deleteFilter}
                onSortChange={onSortChange}
                onFilterChange={onFilterChange}
                sort={sortState}
                selectedFiltersInfos={selectedFiltersInfos}
                isFromWB
              />
              <DisplayNbProducts nbTotalRows={nbTotalRows} />
            </>
          )}
          <ProductListingWB
            productList={products}
            customClassName={styles.content}
            url={originalUrl}
            storeConf={storeConf}
            ratingValue={ratingValue}
            ratingCount={ratingCount}
          />
          {nbTotalRows > pageSize && (
            <div className={`${styles['col-pagination-bottom']} ${styles.pagination}`}>
              <Pagination
                t={t}
                categ={categ}
                nbTotalRows={nbTotalRows}
                page={pageState}
                pageSize={pageSize}
                filterQuery={filterQueryState}
                onChange={onPageChange}
                isMobile={!isDesktop}
              />
            </div>
          )}
        </div>

        <CategFooter
          categ={categ}
          isDesktop={isDesktop}
          isMobileDevice={isMobileDevice}
          descriptionRef={descriptionRef}
          espot={espot}
        />
        <BreadcrumbContainer
          homeLabel={categ.isSpirit ? t('breadcrumbHomeLabelSpirit') : t('breadcrumbHomeLabel')}
          {...categ}
          baseUrl={urlBreadcrumb}
        />
        {(productDetailsModalWBVisible && !isObjectEmpty(productDetailsModalWB)) && (
          <ProductDetailModalWB />
        )}
      </div>
    </>
  );
};

CategViewWB.propTypes = {
  originalUrl: PropTypes.string,
  searchMode: PropTypes.bool,
  searchTerm: PropTypes.string,
  pageSize: PropTypes.number,
  productsForEc: PropTypes.bool,
  ratingValue: PropTypes.number,
  ratingCount: PropTypes.number,
  hashUserFromQuery: PropTypes.string,
  categSSR: PropTypes.object,
  categId: PropTypes.string,
  pageSSR: PropTypes.number,
  sortSSR: PropTypes.number,
  filterQuerySSR: PropTypes.string,
  espot: PropTypes.object
};

export default CategViewWB;

/**
  * Prefetch function  called on server side
  * @param {object} ctx The server side context
  * @param {object} store The server side application store
  * @param {object} props The seo page props
  * @return {object} Return an object containing the component props
  *    layoutOption : object containing the needed page layout (see _app component)
  *    namespaces : an array containing the needed translation namespaces
  */
export const prefetchView = async (ctx, { getState, dispatch, services }, props) => {
  const { query, resolvedUrl } = ctx;
  const {
    root: { storeConf, baseUrl },
    auth: { loggedUser },
    cart: { content: { storeIdForPrice: storeIdForPriceInCart } },
    gui: { storeIdForPrice: storeIdForPriceInStore }
  } = getState();
  const params = new URLSearchParams(resolvedUrl.split('?')[1]);
  const { productsService } = services;
  const { searchTerm = '' } = query;
  const isEC = !!loggedUser?.isEc;
  const { storeId } = props;
  const storeIdForPrice = storeIdForPriceInStore || storeIdForPriceInCart;

  const sort = normalize(params.get('sort'), DEFAULT_SORT);
  const page = normalize(params.get('page'), DEFAULT_PAGE);
  const filterQuery = params.get('filter') || '';
  const searchMode = !!searchTerm;

  let categ;
  let content;
  let categId;
  let products;
  let filters;
  let htmlBlogPost;
  let htmlBlogPostObject;

  if (props.id) {
    categId = props.id.replace(`-${storeId}`, '');
  } else if (searchMode) {
    categ = { urlKeyword: `search?searchTerm=${searchTerm}` };
  } else {
    const stateCateg = getState().categ;
    categId = stateCateg.categId;
    categ = stateCateg.content;
  }

  if (categId) {
    [content, htmlBlogPost] = await completePromises([
      productsService.getCategWithFiltersAndProducts(
        { storeId, categ: categId, page, size: PAGE_SIZE.LIST, filter: filterQuery, sort, isCategViewWB: true, ec: isEC },
        storeIdForPrice
      ),
      productsService.getBlogPostObject(storeConf.site, categId)
    ]);

    try {
      htmlBlogPostObject = JSON.parse(htmlBlogPost);
    } catch (err) {
      console.log('Failed to parse htmlBlogPost:', err);
      htmlBlogPostObject = { content: productsService.getBlogPost(storeConf.site, categId) };
    }

    products = {
      products: content?.products,
      nbTotalRows: content?.nbTotalRows,
      sortOrder: content?.sortOrder
    };
    categ = {
      ...content?.categ,
      top: content?.top,
      filters: content?.filters,
      ogImage: htmlBlogPostObject?.ogImage
    };
  } else if (searchMode) {
    products = await productsService.getSearchProducts(
      { storeId, searchTerm, page, size: PAGE_SIZE.LIST, filter: filterQuery, sort, ec: isEC },
      storeIdForPrice
    );

    if (products.filters) {
      categ.filters = products.filters;
    }
  }

  const categInfo = {
    ...categ,
    ...products,
    filters: parseFilters(categ.filters || {}, filterQuery, storeId),
    page,
    sort,
    searchTerm
  };

  dispatch(setCategInfos({
    categId,
    content: categInfo,
    espot: htmlBlogPostObject?.content,
    filterQuery,
    filters
  }));

  let specialImg;
  if (searchMode && products) {
    specialImg = products.specialImg;
  } else if (categ) {
    specialImg = categ.specialImg;
  }

  const namespacesRequired = [NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER];

  if (isFeatureEnabled(storeConf, FEATURES.MODULE_CUSTO)) {
    namespacesRequired.push(NS.CUSTOMIZATION);
  }

  if (loggedUser?.isAdmin) {
    namespacesRequired.push(NS.ADMIN);
  }

  return {
    layoutOptions: {
      withScrollButton: true
    },
    namespacesRequired,
    originalUrl: baseUrl,
    specialImg,
    pageSize: PAGE_SIZE.LIST,
    searchTerm,
    searchMode,
    // To know if we got Exclusive Cellar products
    productsForEc: isEC,
    categSSR: categInfo,
    categId: categId,
    espot: htmlBlogPostObject?.content,
    filterQuerySSR: filterQuery,
    pageSSR: page,
    sortSSR: sort
  };
};
